import type { LeagueSummary } from "@softmax/observatory-client/types";
import { reservedCoworldSegments } from "@softmax/web-addresses/namespaces";
import { isUnpublishedLeague, reserved } from "./league-catalog";

export type AddressEntries<T extends LeagueSummary> = {
  byId: Map<string, string>;
  byPath: Map<string, T>;
  retained: Map<string, T>;
};

export function retainLeagueAliases<T extends LeagueSummary>({
  root,
  league,
  aliases,
  entries,
}: {
  root: string;
  league: T;
  aliases: readonly string[];
  entries: AddressEntries<T>;
}): void {
  for (const alias of aliases) {
    if (!alias || reservedCoworldSegments.has(alias)) continue;

    const path = `${root}/${alias}`;

    if (entries.byPath.has(path)) continue;

    const previous = entries.retained.get(path);
    const publicPriority =
      previous && isUnpublishedLeague(previous) && !isUnpublishedLeague(league);

    if (!previous || publicPriority) entries.retained.set(path, league);
  }
}

export function bridgeCoworldRoot<T extends LeagueSummary>({
  from,
  to,
  entries,
}: {
  from: string;
  to: string;
  entries: AddressEntries<T>;
}): void {
  for (const addresses of [entries.byPath, entries.retained]) {
    const candidates = Array.from(addresses);

    for (const [path, league] of candidates) {
      if (path !== from && !path.startsWith(`${from}/`)) continue;

      const alias = `${to}${path.slice(from.length)}`;

      if (
        entries.byPath.has(alias) ||
        entries.retained.has(alias) ||
        reserved.has(alias)
      )
        continue;

      addresses.set(alias, league);
    }
  }
}
